import React from 'react';
import { motion } from 'motion/react';
import { Ticket, CheckCircle2, Lock } from 'lucide-react';
import { Voucher } from '../utils/api';
import { formatPrice } from '../data/products';
import { useAuth } from '../context/AuthContext';

interface VoucherTicketProps {
  voucher: Voucher;
  isApplied: boolean;
  onApply: (voucher: Voucher) => void;
  onShowAuthModal?: () => void;
}

export const VoucherTicket: React.FC<VoucherTicketProps> = ({
  voucher,
  isApplied,
  onApply,
  onShowAuthModal,
}) => {
  const { isLoggedIn } = useAuth();

  const getBenefitLongText = (v: Voucher) => {
    if (v.type === 'percentage') return `Diskon ${v.benefit_data.discount_value}%`;
    if (v.type === 'fixed_amount') return `Potongan ${formatPrice(v.benefit_data.discount_value || 0)}`;
    if (v.type === 'free_shipping') return 'Gratis Ongkir';
    if (v.type === 'gift') return 'Hadiah Gratis';
    return 'Promo Spesial';
  };

  const getValidityText = (v: Voucher) => {
    if (!v.end_date) return 'Tanpa Kedaluwarsa';
    const end = new Date(v.end_date);
    return `Berakhir ${end.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' })}`;
  };

  const isMemberOnly = !!voucher.conditions?.registered_only;
  const isPendingAuth = isMemberOnly && !isLoggedIn;

  return (
    <motion.div
      layout
      className={`relative group border p-6 transition-all duration-300 ${isApplied
        ? isPendingAuth
          ? 'bg-amber-50/20 border-amber-300/80 shadow-sm'
          : 'bg-paper border-brand-wine shadow-sm'
        : 'bg-white border-ink/5 hover:border-brand-wine/40 hover:shadow-md'
        }`}
    >
      <div className="flex items-start gap-6">
        {/* Icon / Indicator */}
        <div className={`w-12 h-12 flex items-center justify-center shrink-0 transition-colors rounded-none ${
          isApplied 
            ? isPendingAuth
              ? 'bg-amber-100 text-amber-800'
              : 'bg-brand-wine text-paper' 
            : 'bg-ink/[0.03] text-ink group-hover:bg-brand-wine group-hover:text-paper'
          }`}>
          {isApplied ? (isPendingAuth ? <Lock size={22} /> : <CheckCircle2 size={24} />) : <Ticket size={24} />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start mb-2 gap-2">
            <div>
              <h4 className={`text-title mb-1 ${isApplied ? (isPendingAuth ? 'text-amber-950' : 'text-brand-wine') : 'text-ink'}`}>
                {getBenefitLongText(voucher)}
              </h4>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-label text-ink/40 font-mono">
                  {voucher.code}
                </p>
                {voucher.conditions?.registered_only && (
                  <span className="inline-flex items-center gap-1 text-[8px] font-black tracking-[0.15em] uppercase text-[#722F38] bg-[#722F38]/5 border border-[#722F38]/10 px-2 py-0.5 select-none">
                    <Lock size={8} className="shrink-0" />
                    Khusus Member
                  </span>
                )}
                {voucher.conditions?.active_days && voucher.conditions.active_days.length > 0 && voucher.conditions.active_days.length < 7 && (() => {
                  const days = voucher.conditions.active_days;
                  const isWeekend = days.length === 2 && days.includes(0) && days.includes(6);
                  const isWeekdays = days.length === 5 && !days.includes(0) && !days.includes(6);
                  const dayNames = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
                  const label = isWeekend ? 'Khusus Weekend' : isWeekdays ? 'Khusus Hari Kerja' : `Hari: ${days.map(d => dayNames[d]).join(', ')}`;
                  const isTodayActive = days.includes(new Date().getDay());
                  return (
                    <span className={`inline-flex items-center gap-1 text-[8px] font-bold tracking-wider uppercase px-2 py-0.5 select-none border ${
                      isTodayActive
                        ? 'text-emerald-700 bg-emerald-50 border-emerald-200'
                        : 'text-amber-800 bg-amber-50 border-amber-200'
                    }`}>
                      {label} {!isTodayActive && '(Belum Aktif Hari Ini)'}
                    </span>
                  );
                })()}
              </div>
            </div>
            {isApplied && (
              isPendingAuth ? (
                <span className="text-[8px] font-bold bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 flex items-center gap-1 uppercase tracking-wider shrink-0">
                  <Lock size={9} /> Tersimpan (Perlu Login)
                </span>
              ) : (
                <span className="text-label bg-brand-wine text-paper px-2 py-0.5 shrink-0">
                  Terpasang
                </span>
              )
            )}
          </div>

          <div className="space-y-3.5 mt-4">
            <p className="text-body-fine">
              Minimal belanja {formatPrice(voucher.conditions?.min_purchase || 0)}. {voucher.description}
            </p>
            {/* Row 1: Validity Indicator */}
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-black text-muted/50 uppercase tracking-widest leading-none select-none">
                {getValidityText(voucher)}
              </span>
            </div>

            {/* Row 2: Action details for Applied Pending Auth */}
            {isApplied && isPendingAuth && (
              <div className="pt-3 border-t border-amber-200/60 flex items-center justify-between gap-2">
                <p className="text-[9px] text-amber-900/80 font-medium">
                  Aktif otomatis saat Anda masuk akun
                </p>
                <button
                  onClick={() => {
                    if (onShowAuthModal) {
                      onShowAuthModal();
                    } else {
                      window.location.href = '/account';
                    }
                  }}
                  className="text-[10px] font-black text-brand-wine hover:underline underline-offset-4 flex items-center gap-1 uppercase tracking-wider shrink-0"
                >
                  Masuk Sekarang →
                </button>
              </div>
            )}

            {/* Row 3: Action Button (Only rendered when NOT applied) */}
            {!isApplied && (
              <div className="pt-3.5 border-t border-ink/5">
                <div className="flex justify-end items-center gap-3">
                  {isPendingAuth ? (
                    <>
                      <button
                        onClick={() => {
                          if (onShowAuthModal) {
                            onShowAuthModal();
                          } else {
                            window.location.href = '/account';
                          }
                        }}
                        className="text-[9px] font-bold text-ink/40 hover:text-brand-wine uppercase tracking-wider"
                      >
                        Masuk
                      </button>
                      <button
                        onClick={() => onApply(voucher)}
                        className="text-[10px] font-black text-brand-wine hover:underline underline-offset-4 flex items-center gap-1 uppercase tracking-wider"
                      >
                        <Lock size={10} /> Simpan & Pasang
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => onApply(voucher)}
                      className="text-[10px] font-black text-brand-wine hover:underline underline-offset-4 uppercase tracking-wider"
                    >
                      Gunakan
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Decorative Cutouts for Ticket Feel - Kept as squares for Zero Radius doctrine */}
      <div className="absolute top-1/2 -left-1.5 w-3 h-3 bg-white border border-ink/10 -translate-y-1/2" />
      <div className="absolute top-1/2 -right-1.5 w-3 h-3 bg-white border border-ink/10 -translate-y-1/2" />
    </motion.div>
  );
};
